import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import api from '../utils/api';
import { toast } from 'sonner';
import CosmicBackground from '../components/CosmicBackground';
import Logo from '../components/Logo';
import LanguageSelector from '../components/LanguageSelector';
import { useLanguage } from '../contexts/LanguageContext';
import { useTranslation } from '../utils/translations';
import { Eye, EyeOff } from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { t } = useTranslation(language);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
  });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!acceptedTerms) {
      toast.error('Trebuie să accepți Termenii și condițiile');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/auth/signup', {
        ...formData,
        language: language,
      });

      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      // Keep the guest token in sessionStorage.
      // Dashboard will reveal the answer once, after authentication is fully ready.

      toast.success(t('welcomeToUniverse'));
      navigate('/dashboard', { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('signupFailed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-between relative">
      <div className="flex items-center justify-center flex-1 relative">
        <CosmicBackground />
        <div
          className="fixed inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-violet-900/20 via-slate-900 to-slate-950 pointer-events-none"
          style={{ zIndex: 1 }}
        />

        <div className="fixed top-4 right-4 z-20">
          <LanguageSelector />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative z-10 w-full max-w-md px-6 py-10"
        >
          <div className="glass-card p-8">
            <div className="flex flex-col items-center mb-8">
              <Logo size="lg" showText={false} className="mb-4" />
              <h1 className="font-heading text-3xl font-bold text-white">
                {t('beginJourney')}
              </h1>
              <p className="text-slate-400 mt-2 text-center">
                {t('connectWithCosmicWisdom')}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('fullName')}
                </label>
                <input
                  type="text"
                  required
                  value={formData.full_name}
                  onChange={(e) =>
                    setFormData({ ...formData, full_name: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 transition-all"
                  placeholder={t('fullName')}
                  data-testid="fullname-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('email')}
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                  className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 transition-all"
                  placeholder="your@email.com"
                  data-testid="email-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  {t('password')}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    className="w-full px-4 py-3 bg-slate-950/50 border border-white/10 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/50 transition-all"
                    placeholder="••••••••"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                    data-testid="toggle-password"
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '12px', marginBottom: '12px', fontSize: '14px' }}>
                <label className="text-slate-300">
                  <input
                    type="checkbox"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    style={{ marginRight: '8px' }}
                  />
                  Sunt de acord cu{' '}
                  <Link
                    to="/terms"
                    className="text-violet-400 hover:text-violet-300 underline"
                  >
                    Termenii și condițiile
                  </Link>
                </label>
              </div>

              <button
                type="submit"
                disabled={loading || !acceptedTerms}
                className="w-full py-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white rounded-full font-medium hover:from-violet-500 hover:to-purple-500 transition-all cosmic-glow disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="signup-submit-button"
              >
                {loading ? t('signingUp') : t('createAccount')}
              </button>
            </form>

            <p className="mt-6 text-center text-slate-400 text-sm">
              {t('alreadyHaveAccount')}{' '}
              <Link
                to="/login"
                className="text-violet-400 hover:text-violet-300 font-medium"
                data-testid="login-link"
              >
                {t('signIn')}
              </Link>
            </p>
          </div>
        </motion.div>
      </div>

      <div
        style={{
          textAlign: 'center',
          padding: '20px',
          fontSize: '14px',
          opacity: 0.8,
          position: 'relative',
          zIndex: 10,
        }}
      >
        <Link to="/terms">Termeni și condiții</Link> |{' '}
        <Link to="/privacy">Confidențialitate</Link> |{' '}
        <Link to="/disclaimer">Declinare a responsabilității</Link> |{' '}
        <Link to="/contact">Contact</Link>
      </div>
    </div>
  );
};

export default Signup;
